import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  platformApi,
  type PlatformTenant,
  type SupportPermission,
  type SupportSession,
} from '@/services/platform/platform.http'
import { activateSupportContext } from '@/security/supportContext'

const tone: Record<string, string> = {
  active: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  requested: 'bg-slate-100 text-slate-700',
  rejected: 'bg-red-100 text-red-700',
  revoked: 'bg-slate-200 text-slate-700',
  expired: 'bg-slate-200 text-slate-700',
}

export function PlatformSupportSessionsPage() {
  const { user, hasRole } = useAuth()
  const [items, setItems] = useState<SupportSession[]>([])
  const [tenants, setTenants] = useState<PlatformTenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [target, setTarget] = useState('')
  const [ticket, setTicket] = useState('')
  const [reason, setReason] = useState('')
  const [duration, setDuration] = useState(30)
  const [write, setWrite] = useState(false)
  const load = useCallback(async () => {
    try {
      const [sessions, tenantPage] = await Promise.all([
        platformApi.listSupportSessions(),
        platformApi.listTenants({ page: 1, page_size: 100, status: 'active' }),
      ])
      setItems(sessions.items)
      setTenants(tenantPage.items)
      setError('')
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load support sessions')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const request = async (event: FormEvent) => {
    event.preventDefault()
    const permissions: SupportPermission[] = ['txh.support.data.read']
    if (write) permissions.push('txh.support.data.write')
    try {
      await platformApi.createSupportSession({
        target_tenant_id: target.trim(),
        ticket: ticket.trim(),
        reason: reason.trim(),
        duration_minutes: duration,
        permissions,
      })
      setTarget('')
      setTicket('')
      setReason('')
      setWrite(false)
      await load()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to request support access')
    }
  }
  const decide = async (id: string, approved: boolean) => {
    try {
      await platformApi.decideSupportSession(
        id,
        approved,
        approved ? 'Approved for stated support case' : 'Support request rejected'
      )
      await load()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to record decision')
    }
  }
  const activate = async (id: string) => {
    try {
      activateSupportContext(await platformApi.activateSupportSession(id))
      await load()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to activate support access')
    }
  }
  const revoke = async (id: string) => {
    try {
      await platformApi.revokeSupportSession(id, 'Support work completed')
      await load()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to revoke support access')
    }
  }

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Tenant Support Access</h1>
        <p className="text-text-secondary">
          Temporary access to a tenant’s business data for an approved support case.
        </p>
      </header>
      <section className="card mb-6 border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
        <h2 className="font-semibold">How this works</h2>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>
            Enable MFA in Settings, then sign in with an authenticator code within five minutes.
          </li>
          <li>Select an active tenant and provide a real support ticket and reason.</li>
          <li>
            A different CML Admin approves the request; requesters cannot approve their own access.
          </li>
          <li>
            Activation issues a short-lived credential for support-capable tenant tools. It does not
            change tenant ownership or turn normal platform pages into tenant pages.
          </li>
          <li>
            Every supported action is attributed and audited. Revoke access when the task is
            complete.
          </li>
        </ol>
        <p className="mt-2">
          CML Monitor can request read-only access. Only CML Admin can request write access.
        </p>
      </section>
      <form
        className="card mb-6 grid gap-4 p-5 md:grid-cols-2"
        onSubmit={event => void request(event)}
      >
        <h2 className="font-semibold md:col-span-2">Request access</h2>
        <label className="text-sm">
          Target tenant
          <select
            required
            className="input mt-1 w-full"
            value={target}
            onChange={e => setTarget(e.target.value)}
          >
            <option value="">Select tenant</option>
            {tenants.map(tenant => (
              <option key={tenant.tenant_id} value={tenant.tenant_id}>
                {tenant.tenant_name} ({tenant.tenant_code})
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          Ticket
          <input
            required
            minLength={2}
            className="input mt-1 w-full"
            value={ticket}
            onChange={e => setTicket(e.target.value)}
            placeholder="SUP-1234"
          />
        </label>
        <label className="text-sm md:col-span-2">
          Reason
          <textarea
            required
            minLength={10}
            className="input mt-1 w-full"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </label>
        <label className="text-sm">
          Duration
          <select
            className="input mt-1 w-full"
            value={duration}
            onChange={e => setDuration(Number(e.target.value))}
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>60 minutes</option>
          </select>
        </label>
        {hasRole('cml_admin') && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={write} onChange={e => setWrite(e.target.checked)} />
            Request write access
          </label>
        )}
        <div className="md:col-span-2">
          <button className="btn btn-primary" type="submit">
            Request support access
          </button>
        </div>
      </form>
      {error && (
        <div role="alert" className="mb-4 text-red-700">
          {error}
        </div>
      )}
      {loading && <p>Loading support sessions…</p>}
      <div className="space-y-3">
        {items.map(item => (
          <article className="card p-5" key={item.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold">
                  {item.case_ticket} · Tenant {item.target_tenant_id}
                </h2>
                <p className="text-sm text-text-secondary">{item.reason}</p>
                <p className="text-xs text-text-tertiary">
                  {item.duration_minutes} minutes ·{' '}
                  {item.requested_permissions.includes('txh.support.data.write')
                    ? 'read/write'
                    : 'read-only'}{' '}
                  · requested {new Date(item.created_at).toLocaleString()}
                </p>
              </div>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${tone[item.status] ?? tone.requested}`}
              >
                {item.status}
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {item.status === 'requested' &&
                hasRole('cml_admin') &&
                item.requester_actor_id !== user?.id && (
                  <>
                    <button className="btn btn-primary" onClick={() => void decide(item.id, true)}>
                      Approve
                    </button>
                    <button className="btn" onClick={() => void decide(item.id, false)}>
                      Reject
                    </button>
                  </>
                )}
              {item.status === 'approved' && item.requester_actor_id === user?.id && (
                <button className="btn btn-primary" onClick={() => void activate(item.id)}>
                  Activate
                </button>
              )}
              {item.status === 'active' &&
                (item.requester_actor_id === user?.id || hasRole('cml_admin')) && (
                  <button className="btn" onClick={() => void revoke(item.id)}>
                    Revoke
                  </button>
                )}
            </div>
          </article>
        ))}
      </div>
    </>
  )
}
